import cx from "classnames"
import * as React from "react"
import ErrorIcon from "../images/svg/error.svg"

export const classNameWithError = (hasError = false, className = "") =>
  cx(hasError && "outline outline-2 outline-red-700 duration-300", className)

const inputStyle =
  "placeholder:text-grey-lead px-6 rounded-button text-base font-mono disabled:outline-none outline outline-1 outline-black -outline-offset-1 focus:outline-2 focus:outline-blue-500"

const Input = ({ className, ...props }) => (
  <input
    className={cx("border-0 w-full h-12", inputStyle, className)}
    {...props}
  />
)

const Textarea = ({ className, ...props }) => (
  <textarea
    className={cx("border-0 w-full h-40 py-4", inputStyle, className)}
    {...props}
  />
)

const FormCharacterCount = ({ limit, text = "", className }) => {
  const { useEffect, useState } = React
  const [inputText, setInputText] = useState(text)
  const [characterLimit] = useState(limit)

  const isOffLimit = (inputText?.length ?? 0) > characterLimit

  useEffect(() => {
    setInputText(text)
  }, [text])

  return (
    <span
      className={cx(className, isOffLimit ? "text-red-500" : "text-lead-grey")}
    >
      {inputText?.length}/{characterLimit}
    </span>
  )
}

const defaultStyles =
  "h-4 bg-transparent border-0 no-underline absolute right-0 inset-y-0"
const focusStyles =
  "focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-500 focus-visible:outline-offset-2 focus-visible:rounded"
const FormFieldInput = ({
  id,
  name,
  className,
  type = 'text',
  maxLength = 100,
  helpText,
  charCount,
  hasError,
  autoCapitalize = "none",
  autoComplete = "off",
  autoCorrect = "off",
  inputClassName,
  prefix,
  postfix,
  disabled,
  helperHeight,
  ...props
}) => {
  return (
    <div
      className={cx(
        "text-black text-left",
        hasError && "text-red-500",
        className
      )}
      {...props}
    >
      <div className="relative flex items-center">
        {type === "textarea" ? (
          <Textarea
            id={id}
            name={name}
            maxLength={maxLength}
            className={classNameWithError(hasError, inputClassName)}
            aria-controls={helpText?.id}
            aria-invalid={hasError}
            aria-describedby={helpText?.id}
            autoCapitalize={autoCapitalize}
            autoComplete={autoComplete}
            autoCorrect={autoCorrect}
            disabled={disabled}
            {...props}
          />
        ) : (
          <Input
            id={id}
            name={name}
            type={type || "text"}
            maxLength={maxLength}
            className={classNameWithError(hasError, inputClassName)}
            aria-controls={helpText?.id}
            aria-invalid={hasError}
            aria-describedby={helpText?.id}
            autoCapitalize={autoCapitalize}
            autoComplete={autoComplete}
            autoCorrect={autoCorrect}
            disabled={disabled}
            {...props}
          />
        )}

        {disabled && (
          <div className="absolute w-full h-12 bg-grey-button-background rounded-lg" />
        )}
        {hasError && (
          <div
            className={cx(
              defaultStyles,
              focusStyles,
              type === "textarea" ? "mt-5 mr-2.5" : "my-auto mr-2.5",
              "cursor-auto"
            )}
          >
            <ErrorIcon />
          </div>
        )}
      </div>
      <div
        className={cx(
          hasError && "text-red-500",
          "h-4 my-1 mobile:my-2",
          helperHeight === "auto" && "min-h-0 mt-2",
          "text-sm-mobile flex items-center justify-between text-grey-lead mx-0"
        )}
      >
        {helpText && (
          <span
            id={helpText.id}
            className={helpText?.className}
            role={hasError ? "alert" : "status"}
          >
            {helpText.text}
          </span>
        )}
        {charCount && charCount?.text && (
          <FormCharacterCount
            limit={charCount.limit}
            text={charCount.text}
            role={hasError ? "alert" : "status"}
          />
        )}
      </div>
    </div>
  )
}

FormFieldInput.displayName = "FormFieldInput"

export default FormFieldInput
